import { useState, useEffect } from 'react';

import FeedReactions from 'models/feedReactions';
import { useDispatch } from 'react-redux';

import { REACTIONS, ERROR_MESSAGE_ACTION } from 'constants/ProjectConstants';

import { addReactionToFeed, removeReactionToFeed } from 'services/feed';
import { FEED_TYPES } from 'services/helpers/feed';
import { showErrorSnackbar } from 'services/snackbar';

export function updateDataOnAddReaction({ reactions, reactionType }) {
  const backupReactions = { ...reactions };
  const tempReactions = { ...reactions };
  if (reactionType === REACTIONS.like) {
    tempReactions.likesCount += 1;
    tempReactions.isLiked = true;
  } else if (reactionType === REACTIONS.applause) {
    tempReactions.applauseCount += 1;
    tempReactions.isApplauded = true;
  }
  return { backupReactions, tempReactions };
}

export function updateDataOnRemoveReaction({ reactions, reactionType }) {
  const backupReactions = { ...reactions };
  const tempReactions = { ...reactions };
  if (reactionType === REACTIONS.like) {
    tempReactions.likesCount = tempReactions.likesCount - 1 < 0 ? 0 : tempReactions.likesCount - 1; // <- making sure that if likes count reaches negative, we're offsetting it with 0
    tempReactions.isLiked = false;
  } else if (reactionType === REACTIONS.applause) {
    tempReactions.applauseCount -= 1;
    tempReactions.isApplauded = false;
  }
  return { backupReactions, tempReactions };
}

export default function useReactions(reactionsData, feedId, feedType = FEED_TYPES.post) {
  const dispatch = useDispatch();
  const [reactions, setReactions] = useState(reactionsData);
  useEffect(() => {
    const newReactions = FeedReactions.getObject({
      emotionsCount: reactionsData.emotionsCount,
      commentsCount: reactionsData.commentsCount,
      isLiked: reactionsData.isLiked,
      isApplauded: reactionsData.isApplauded,
      likesCount: reactionsData.likesCount,
      applauseCount: reactionsData.applauseCount,
      isCommented: reactionsData.isCommented,
    });
    setReactions(newReactions);
  }, [
    reactionsData.commentsCount,
    reactionsData.emotionsCount,
    reactionsData.isLiked,
    reactionsData.applauseCount,
    reactionsData.likesCount,
    reactionsData.isApplauded,
    reactionsData.isCommented,
  ]);

  function onAddReaction({ reactionType, setIsLiking = () => {} }) {
    const { backupReactions, tempReactions } = updateDataOnAddReaction({ reactions, reactionType });
    setReactions(tempReactions);
    setIsLiking(true);
    addReactionToFeed({ feedId, reactionType, feedType })
      .then((reactions) => {
        setReactions(reactions);
      })
      // TODO: Maybe get response from backend which contains new reactions on error as well
      // when there is 500 error, then do this backupReactions logic, otherwise use the latest reactions
      .catch((err) => {
        // We want to restore the reactions to original state when there is an error
        setReactions(backupReactions);
        dispatch(showErrorSnackbar(ERROR_MESSAGE_ACTION));
      })
      .finally(() => {
        setIsLiking(false);
      });
  }

  function onRemoveReaction({ reactionType, setIsLiking = () => {} }) {
    const { backupReactions, tempReactions } = updateDataOnRemoveReaction({
      reactions,
      reactionType,
    });
    setReactions(tempReactions);
    setIsLiking(true);
    removeReactionToFeed({ feedId, reactionType, feedType })
      .then((reactions) => {
        setReactions(reactions);
      })
      // TODO: Maybe get response from backend which contains new reactions on error as well
      // when there is 500 error, then do this backupReactions logic, otherwise use the latest reactions
      .catch((err) => {
        // We want to restore the reactions to original state when there is an error
        setReactions(backupReactions);
        dispatch(showErrorSnackbar(ERROR_MESSAGE_ACTION));
      })
      .finally(() => {
        setIsLiking(false);
      });
  }

  return {
    reactions,
    setReactions,
    onAddReaction,
    onRemoveReaction,
  };
}
